import React, { FC, useCallback, useState } from 'react';
import { Modal, DatePicker, Form, message, Button, Select } from 'antd';
import { connect } from 'react-redux';
import moment from 'moment';
import {
	siteSurveysRestCrudThunksForBuyer,
	siteSurveysRestCrudThunksForSupplier,
} from '../../thunks/site_surveys_thunks';
import {
	walkthroughsRestCrudThunksForBuyer,
	walkthroughsRestCrudThunksForSupplier,
} from '../../thunks/walkthroughs_thunks';
import { withRouter } from 'react-router';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import {
	fetchSupplierFacilitiesInPrivateNetworkForBuyer,
	supplierFacilitiesRestCrudThunksForBuyer,
} from '../../thunks/supplier_facilities_thunks';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { WALKTHROUGHS_CRUD_ACTION_CREATORS } from '../../actions/walkthroughs_actions';
import OWForm from '../ow_form/OWForm';

const FormItem = Form.Item;
export const DATE_FORMAT = 'MMMM D, YYYY h:mm a';

const disabledDate = (current) => {
	// Can not select days before today and today
	return current < moment().startOf('day');
};

const FORM_NAME = 'schedule_walkthrough_form';

const ScheduleWalkthroughModalForm: FC<any> = ({
	createWalkthrough,
	getSiteSurvey,
	currentUser,
	onSuccess,
	onCancel,
	userType,
	supplierFacilities,
	fetchMultipleSupplierFacilitiesInPrivateNetwork,
	fetchSupplierFacilitiesInPrivateNetwork,
	getSiteSurveys,
	siteSurveys,
	siteSurveyId,
}): React.ReactElement => {
	const [loading, setLoading] = useState(false);

	const onSubmit = useCallback(
		(values) => {
			setLoading(true);
			getSiteSurvey(values.siteSurveyId)
				.then((survey) => {
					const updateValues = {
						...values,
						title: survey.title + ' walkthrough',
						status: 'incomplete',
						locationId: survey.locationId,
						siteSurveyId: survey.id,
						scheduledBy: currentUser.email,
					};

					createWalkthrough(updateValues)
						.then((walkthrough) => {
							onSuccess && onSuccess(walkthrough);
						})
						.catch((err) => message.error(err))
						.finally(() => setLoading(false));
				})
				.catch((err) => {
					message.error(err);
					setLoading(false);
				});
		},
		[createWalkthrough, currentUser.email, getSiteSurvey, onSuccess]
	);

	return (
		<Modal
			visible={true}
			width={600}
			title="Schedule Walkthrough"
			onCancel={onCancel}
			footer={[
				<Button onClick={onCancel} size="large">
					Cancel
				</Button>,
				<Button
					type="primary"
					size="large"
					key="submit"
					htmlType="submit"
					form={FORM_NAME}
					loading={loading}
				>
					Schedule
				</Button>,
			]}
			closable={false}
		>
			<OWForm
				layout="vertical"
				onFinish={onSubmit}
				id={FORM_NAME}
				actionCreators={WALKTHROUGHS_CRUD_ACTION_CREATORS}
			>
				<Form.Item
					label="Site Survey"
					name="siteSurveyId"
					rules={[
						{
							required: true,
							message: 'Please select a site survey.',
						},
					]}
					hidden={!!siteSurveyId}
					initialValue={siteSurveyId}
				>
					<OWAsyncSelect
						disabled={siteSurveyId}
						stateSlice={siteSurveys}
						targetCollectionName="siteSurveyScheduleWalkthrough"
						fetchMultiple={(ids, targetCollectionName) => {}}
						fetchData={(
							searchText,
							targetCollectionName,
							pagination,
							sorting,
							filters,
							addToTargetCollection
						) =>
							getSiteSurveys(
								{ title: searchText || undefined },
								targetCollectionName,
								pagination,
								sorting,
								filters,
								addToTargetCollection
							)
						}
						renderRecord={(siteSurvey) => (
							<Select.Option key={siteSurvey.id} value={siteSurvey.id}>
								{siteSurvey.title}
							</Select.Option>
						)}
						initialPagination={{ current: 1, pageSize: 10 }}
					/>
				</Form.Item>

				<FormItem
					label="Due By"
					name="dueAt"
					rules={[
						{
							required: true,
							message: 'Please select the walkthrough due date.',
						},
					]}
				>
					<DatePicker
						disabledDate={disabledDate}
						style={{ width: '100%' }}
						showTime={true}
						format={DATE_FORMAT}
					/>
				</FormItem>
				{userType === ROLE_TYPES.BUYER ? (
					<Form.Item label="Supplier" name="supplierFacilityId">
						<OWAsyncSelect
							stateSlice={supplierFacilities}
							targetCollectionName="walkthroughSupplierFacilitiesInPrivateNetwork"
							fetchMultiple={(ids, targetCollectionName) => {
								fetchMultipleSupplierFacilitiesInPrivateNetwork(ids, targetCollectionName);
							}}
							fetchData={(
								searchText,
								targetCollectionName,
								pagination,
								sorting,
								filters,
								addToTargetCollection
							) => {
								fetchSupplierFacilitiesInPrivateNetwork(
									{ name: searchText, offset: 0, limit: 100 },
									targetCollectionName,
									pagination,
									sorting,
									filters,
									addToTargetCollection
								);
							}}
							renderRecord={(supplierFacility) => (
								<Select.Option key={supplierFacility.id} value={supplierFacility.id}>
									{supplierFacility.name}
								</Select.Option>
							)}
						/>
					</Form.Item>
				) : null}
			</OWForm>
		</Modal>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
	supplierFacilities: state.supplier_facilities,
	siteSurveys: state.site_surveys,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	getSiteSurvey: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? siteSurveysRestCrudThunksForSupplier.readOne(id)
				: siteSurveysRestCrudThunksForBuyer.readOne(id)
		),
	getSiteSurveys: (
		params,
		targetCollectionName = null,
		pagination = null,
		sorting = null,
		filters = null,
		addToTargetCollection = false
	) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? siteSurveysRestCrudThunksForSupplier.readLite(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
				: siteSurveysRestCrudThunksForBuyer.readLite(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
		),
	createWalkthrough: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? walkthroughsRestCrudThunksForSupplier.create(entity)
				: walkthroughsRestCrudThunksForBuyer.create(entity)
		),
	fetchMultipleSupplierFacilitiesInPrivateNetwork: (ids, targetCollectionName) =>
		dispatch(supplierFacilitiesRestCrudThunksForBuyer.readMultiple(ids, targetCollectionName)),
	fetchSupplierFacilitiesInPrivateNetwork: (params) =>
		dispatch(
			fetchSupplierFacilitiesInPrivateNetworkForBuyer(
				params,
				'walkthroughSupplierFacilitiesInPrivateNetwork'
			)
		),
});

const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(ScheduleWalkthroughModalForm)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
